import type {
  FetchError,
  StoreStockTrackingBulkResponse,
  StoreStockTrackingCandidatesListQuery,
  StoreStockTrackingCandidatesResponse,
  StoreStockTrackingItemResponse,
  StoreStockTrackingListQuery,
  StoreStockTrackingListResponse,
  StoreUntrackStockTrackingResponse,
  StoreUpsertStockTracking
} from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import { isDue } from "@/lib/stock-item";

import { stockTrackingQueryKeys } from "./queryKeys";

/**
 * Client data layer for the opt-in **salon stock-tracking** feature (#71, over
 * api#148): `queryOptions` for the tracked-items list and the order-history seed
 * candidates, plus upsert / bulk-seed / untrack mutations.
 *
 * Count/level edits are **optimistic** - the inline editor stays snappy and the
 * `due_count` hero stat is recomputed locally so the home widget can't flash a
 * stale number - then reconciled by invalidating the tracked list on settle. A
 * track/untrack also invalidates the candidate list (the variant moves between
 * "tracked" and "seedable"). The optimistic `is_due` recompute uses the shared
 * `stock-item` predicate so it can't drift from the server's rule.
 */

// ============================================================
// Queries
// ============================================================

/**
 * The salon's tracked items. Small (opt-in) list; the default (no `due` filter)
 * fetch is what the "My Stock" editor reads, and its `due_count` feeds the hero
 * stat. Kept fairly fresh since counts change often.
 */
export function stockTrackingQuery(query?: StoreStockTrackingListQuery) {
  return queryOptions({
    queryKey: stockTrackingQueryKeys.list(query),
    queryFn: () => sdk.store.stockTracking.list(query),
    staleTime: 60 * 1000
  });
}

export function useStockTracking(
  query?: StoreStockTrackingListQuery,
  options?: { enabled?: boolean }
) {
  return useQuery({ ...stockTrackingQuery(query), ...options });
}

/**
 * The "track from your orders" seed candidates: purchasable variants from the
 * salon's order history they don't already track (server-excluded).
 */
export function stockTrackingCandidatesQuery(
  query?: StoreStockTrackingCandidatesListQuery
) {
  return queryOptions({
    queryKey: stockTrackingQueryKeys.candidates(query),
    queryFn: () => sdk.store.stockTracking.candidates(query),
    staleTime: 60 * 1000
  });
}

export function useStockCandidates(
  query?: StoreStockTrackingCandidatesListQuery,
  options?: { enabled?: boolean }
) {
  return useQuery({ ...stockTrackingCandidatesQuery(query), ...options });
}

// ============================================================
// Optimistic list patching
// ============================================================

/**
 * Apply a full-state upsert to a cached list. Only patches a variant already in
 * the list (a count/level edit) - a *new* track can't be hydrated client-side
 * (no variant display), so it falls through to the on-settle invalidation.
 * `due_count` is adjusted by the item's due-state delta so it stays correct even
 * on a paginated/filtered page.
 */
function applyUpsertToList(
  list: StoreStockTrackingListResponse,
  body: StoreUpsertStockTracking
): StoreStockTrackingListResponse {
  const index = list.stock_items.findIndex(
    (item) => item.variant_id === body.variant_id
  );
  if (index === -1) {
    return list;
  }

  const previous = list.stock_items[index];
  const reorder_level = body.reorder_level ?? 1;
  // Full-state: an omitted `on_hand` resets to null ("not counted").
  const on_hand = body.on_hand === undefined ? null : body.on_hand;
  const is_due = isDue(reorder_level, on_hand);

  const stock_items = [...list.stock_items];
  stock_items[index] = { ...previous, reorder_level, on_hand, is_due };

  return {
    ...list,
    stock_items,
    due_count: list.due_count + (is_due ? 1 : 0) - (previous.is_due ? 1 : 0)
  };
}

/** Remove a variant from a cached list, keeping `count`/`due_count` honest. */
function applyUntrackToList(
  list: StoreStockTrackingListResponse,
  variantId: string
): StoreStockTrackingListResponse {
  const removed = list.stock_items.find(
    (item) => item.variant_id === variantId
  );
  if (!removed) {
    return list;
  }

  return {
    ...list,
    stock_items: list.stock_items.filter(
      (item) => item.variant_id !== variantId
    ),
    count: Math.max(0, list.count - 1),
    due_count: removed.is_due ? Math.max(0, list.due_count - 1) : list.due_count
  };
}

// ============================================================
// Mutations
// ============================================================

interface StockListMutationContext {
  /** Every tracked-list cache entry as it was before the optimistic patch. */
  previousLists: [QueryKey, StoreStockTrackingListResponse | undefined][];
}

/** Cancel in-flight list fetches and snapshot every list cache for rollback. */
async function snapshotLists(
  queryClient: QueryClient
): Promise<StockListMutationContext> {
  await queryClient.cancelQueries({
    queryKey: stockTrackingQueryKeys.lists()
  });
  const previousLists =
    queryClient.getQueriesData<StoreStockTrackingListResponse>({
      queryKey: stockTrackingQueryKeys.lists()
    });
  return { previousLists };
}

/** Restore the pre-mutation list caches after a failed optimistic update. */
function rollbackLists(
  queryClient: QueryClient,
  context?: StockListMutationContext
) {
  context?.previousLists.forEach(([key, data]) => {
    queryClient.setQueryData(key, data);
  });
}

/** Reconcile: refetch the tracked list (rows + real timestamps) and candidates. */
function invalidateStockTracking(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: stockTrackingQueryKeys.lists() });
  queryClient.invalidateQueries({
    queryKey: stockTrackingQueryKeys.candidates()
  });
}

/**
 * Track a variant or edit its level/count (full-state upsert). Optimistic for an
 * existing item; a first-time track relies on the on-settle refetch to hydrate.
 */
export function useUpsertStockTracking() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreStockTrackingItemResponse,
    FetchError,
    StoreUpsertStockTracking,
    StockListMutationContext
  >({
    mutationFn: (body) => sdk.store.stockTracking.upsert(body),
    onMutate: async (body) => {
      const context = await snapshotLists(queryClient);
      queryClient.setQueriesData<StoreStockTrackingListResponse>(
        { queryKey: stockTrackingQueryKeys.lists() },
        (old) => (old ? applyUpsertToList(old, body) : old)
      );
      return context;
    },
    onError: (_error, _body, context) => rollbackLists(queryClient, context),
    onSettled: () => invalidateStockTracking(queryClient)
  });
}

/**
 * Bulk-track variants (cold-start seed). Not optimistic - the seeded rows carry
 * server-hydrated variant display we can't fabricate - so it just reconciles on
 * success.
 */
export function useBulkTrackStock() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreStockTrackingBulkResponse,
    FetchError,
    StoreUpsertStockTracking[]
  >({
    mutationFn: (items) => sdk.store.stockTracking.bulk(items),
    onSuccess: () => invalidateStockTracking(queryClient)
  });
}

/** Untrack a variant. Optimistically drops the row, rolling back on error. */
export function useUntrackStock() {
  const queryClient = useQueryClient();

  return useMutation<
    StoreUntrackStockTrackingResponse,
    FetchError,
    string,
    StockListMutationContext
  >({
    mutationFn: (variantId) => sdk.store.stockTracking.untrack(variantId),
    onMutate: async (variantId) => {
      const context = await snapshotLists(queryClient);
      queryClient.setQueriesData<StoreStockTrackingListResponse>(
        { queryKey: stockTrackingQueryKeys.lists() },
        (old) => (old ? applyUntrackToList(old, variantId) : old)
      );
      return context;
    },
    onError: (_error, _variantId, context) =>
      rollbackLists(queryClient, context),
    onSettled: () => invalidateStockTracking(queryClient)
  });
}
